import { AfterViewInit, Component, ElementRef, HostListener, inject, Input, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { ControlContainer, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ForgeIconButtonModule, ForgeIconModule, ForgeTextFieldModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { CustomValidatorMessage, evaluate, LgdErrorStateMatcher, LgdFormControl, LgdFormControlTextConfig } from "../lgdCommon";

export const enum VisibilityIcon
{
    Off = "visibility_off",
    On = "visibility",
}

export const enum VisibilityTooltip
{
    Off = "Show Password",
    On = "Hide Password",
}


export const enum FieldType
{
    Off = "password",
    On = "text",
}

export const thirtyMinutesInMs = 30000;

@Component({
    selector: "lgd-password",
    templateUrl: "./lgdPassword.tmpl.html",
    styleUrls: [
        "./lgdPassword.component.scss"
    ],
    imports: [
        ForgeTextFieldModule,
        ReactiveFormsModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeTooltipModule,
    ]
})
export class LgdPasswordComponent implements OnInit, AfterViewInit, OnDestroy
{
    private readonly controlContainer = inject(ControlContainer);

    private readonly destructor$ = new Subject<void>();
    @ViewChild("input") readonly inputElement: ElementRef<HTMLInputElement>;

    private _initialized: boolean;

    @Input() readonly controlName: string | number;
    @Input() readonly label: string;
    @Input() readonly hideLabel: boolean;
    @Input() readonly autocomplete: string;

    readonly errorStateMatcher = new LgdErrorStateMatcher();

    customErrors: ReadonlyArray<CustomValidatorMessage>;
    hasCustomValidators: boolean;
    readonly visibilityIcon = signal<VisibilityIcon>(VisibilityIcon.Off);
    readonly visibilityTooltip = signal<VisibilityTooltip>(VisibilityTooltip.Off);
    readonly fieldType = signal<FieldType>(FieldType.Off);
    isCapsLockOn: boolean;

    get initialized(): boolean
    {
        return this._initialized;
    }

    get formGroup(): FormGroup
    {
        return this.controlContainer.control as FormGroup;
    }

    get formControl(): FormControl<string>
    {
        if (!this.formGroup || (this.controlName !== undefined && this.controlName == null))
        {
            throw new Error("Cannot access either formGroup or controlName.");
        }

        const controlNameStr = typeof this.controlName === "string" ? this.controlName : `${this.controlName}`;
        return this.formGroup.get(controlNameStr) as FormControl<string>;
    }

    get lgdFormControl(): LgdFormControl<LgdFormControlTextConfig>
    {
        return LgdFormControl.isLgdFormControl(this.formControl) ? this.formControl : undefined;
    }

    get isInvalid(): boolean
    {
        return this.formControl.touched && this.formControl.invalid;
    }

    get isRequired(): boolean
    {
        return !!this.lgdFormControl && evaluate(this.lgdFormControl.data.required);
    }

    @HostListener("keydown", ["$event"])
    onKeydown($event: KeyboardEvent): void
    {
        this.isCapsLockOn = $event.getModifierState("CapsLock");
    }

    ngOnInit(): void
    {
        this._initialized = true;

        if (this.lgdFormControl)
        {
            this.hasCustomValidators = !!this.lgdFormControl.data.customValidators;

            this.customErrors = this.hasCustomValidators
                ? this.lgdFormControl.data.customValidators.reduce(
                    (l, x) =>
                    {
                        return l.concat(x.getErrorMessage(this.label));
                    },
                    [])
                : [];
        }
    }

    ngAfterViewInit(): void
    {
        if (!this.inputElement)
        {
            console.warn(`LgdTextComponent.ngAfterViewInit - ViewChild inputElement is not defined!`);
        }

        if (this.lgdFormControl)
        {
            this.lgdFormControl.onFocus
                .pipe(takeUntil(this.destructor$))
                .subscribe((shouldFocus: boolean) =>
                {
                    if (shouldFocus)
                    {
                        /**
                         * Calling in a setTimeout to avoid a bug in our current @angular version.
                         */
                        setTimeout(() =>
                        {
                            this.inputElement.nativeElement.focus();
                        });
                    }
                });
        }
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    hasError(id: string): boolean
    {
        return !!this.formControl && this.formControl.hasError(id);
    }

    showErrorMessage(id: string): boolean
    {
        return this.formControl.touched && this.hasError(id);
    }

    onVisibilityClick($event: Event): void
    {
        $event?.stopPropagation();

        if (this.fieldType() === FieldType.Off)
        {
            this.visibilityIcon.set(VisibilityIcon.On);
            this.visibilityTooltip.set(VisibilityTooltip.On);
            this.fieldType.set(FieldType.On);

            setTimeout(
                () =>
                {
                    this.visibilityIcon.set(VisibilityIcon.Off);
                    this.visibilityTooltip.set(VisibilityTooltip.Off);
                    this.fieldType.set(FieldType.Off);
                },
                thirtyMinutesInMs);
        }
        else
        {
            this.visibilityIcon.set(VisibilityIcon.Off);
            this.visibilityTooltip.set(VisibilityTooltip.Off);
            this.fieldType.set(FieldType.Off);
        }
    }
}
